/**
 * ACT2-1108: the chat home while the workspace has no warehouse. Chat opens once one is connected.
 *
 * Who can connect one is the RudderStack permission to create warehouse accounts (ACT2-1276), not the
 * Lookout role, so this reads `useWarehousePermission()`:
 * - allowed: a "Connect a warehouse" action to Settings › Warehouses. It is here, not only in the setup
 *   panel, so the person is never stuck after hiding the panel or when `setup.status` fails;
 * - the check failed or needs a fresh sign-in: the same notice and recovery as Settings › Warehouses;
 * - denied: who may be able to help (`NoWarehouseMemberNotice`).
 */
import { Link } from 'react-router-dom';

import { Button } from '@/_shared/components/ui/button';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useWarehousePermission } from '@/settings/components/warehouseAccount/accountCreatePermission';
import { PermissionNotice } from '@/settings/components/warehouseAccount/PermissionNotice';

import { NoWarehouseMemberNotice } from './NoWarehouseMemberNotice';

const CHAT_OPENS = 'Chat opens once a warehouse is connected.';

export function NoWarehouseHome(): React.ReactElement {
  const { wp } = useWorkspace();
  const permission = useWarehousePermission();

  if (permission.loading) {
    return <p className="text-center text-sm text-muted-foreground">{CHAT_OPENS}</p>;
  }
  if (permission.blocked === null) {
    return (
      <div className="flex flex-col items-center gap-3">
        <p className="text-center text-sm text-muted-foreground">{CHAT_OPENS}</p>
        <Button asChild variant="primary">
          <Link to={wp('/settings/warehouses')}>Connect a warehouse</Link>
        </Button>
      </div>
    );
  }
  if (permission.unknown || permission.reauthRequired) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-center text-sm text-muted-foreground">{CHAT_OPENS}</p>
        <PermissionNotice permission={permission} />
      </div>
    );
  }
  return <NoWarehouseMemberNotice />;
}
